/* eslint-disable react/sort-comp */
import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Condition from '../../services/Condition';
import ConfirmDialog from '../ConfirmDialog';
import CreateDialog from '../CreateDialog';
import { getSorting, ASC_ORDER, DESC_ORDER } from './Sort';
import withTree from './withTree';
import './TableDetails.scss';

const NO_SORT_PROPERTY = { name: '' };
const DEFAULT_ROWS_PER_PAGE = 10;

const sameArrays = (ar1, ar2) => {
  if (!ar1) {
    return !ar2 || (ar2.length === 0);
  }
  if (!ar2 || (ar1.length !== ar2.length)) return false;
  for (let i = ar1.length - 1; i >= 0; i -= 1) {
    if (ar1[i] !== ar2[i]) {
      return false;
    }
  }
  return true;
};

const toObject = (propNames, props, defaultObj) => {
  let same = defaultObj;
  const obj = {};
  propNames.forEach((name) => {
    const value = props[name];
    if (same && (defaultObj[name] !== value)) {
      same = false;
    }
    obj[name] = value;
  });
  return same ? defaultObj : obj;
};

const getStorageItem = (props, key) => {
  const { context = {}, configuration = {} } = props;
  if (configuration.getStorageItem) return configuration.getStorageItem(key);
  if (context.getStorageItem) return context.getStorageItem(key);
  return props.getStorageItem
      ? props.getStorageItem(key)
      : window.localStorage.getItem(key);
};

const setStorageItem = (props, key, item) => {
  const { context = {}, configuration = {} } = props;
  if (configuration.setStorageItem) {
    configuration.setStorageItem(key, item);
  } else if (context.setStorageItem) {
    context.setStorageItem(key, item);
  } else {
    window.localStorage.setItem(key, item);
  }
};

const setNodesProperty = (nodes, p, value) => {
  const l = nodes ? nodes.length : 0;
  for (let i = 0; i < l; i += 1) {
    nodes[i][p] = value;
  }
};

const computeColumnsState = (props, options) => {
  const { viewService, configuration = {} } = props;
  const rowModel = props.models[0];
  const propsByName = {};
  const properties = [];
  const decorateTableProperty = (col, iCol) => {
    // Decorate column property with processed column properties
    const { valueRenderer, type } = col;
    let editor;
    let editorCmp;
    let rendererCmp;
    if (valueRenderer) {
      const Comp = viewService.getComponent(valueRenderer.component);
      if (!Comp) {
        console.error(`No component for renderer ${valueRenderer.component} for column ${col.name}`);
      }
      const ctx = {};
      rendererCmp = rprops => (
        <Comp {...rprops} configuration={valueRenderer.configuration} ctx={ctx} />
      );
    } else {
      editor = configuration.editors && configuration.editors[type] || viewService.getEditor(type, props);
      editorCmp = editor && editor.getTableCellComponent(true);
      rendererCmp = editor && editor.getTableCellComponent(false);
    }
    const numeric = type === 'integer' || type === 'number';
    const colExt = {
      ...col,
      editor,
      editorCmp,
      rendererCmp,
      valueOnly: valueRenderer ? valueRenderer.valueOnly : undefined,
      origEditorCmp: editorCmp,
      origRendererCmp: rendererCmp,
      numeric,
      align: col.align || (numeric ? 'right' : 'left'),
      canSort: !editor ? false : (col.canSort === undefined || col.canSort),
    };
    return options.column && options.column(colExt, iCol) || colExt;
  };

  const columns = viewService.computeViewProperties(
    configuration && configuration.columns,
    rowModel.properties,
    propsByName,
    properties,
  ).map(decorateTableProperty);

  const createProperties = configuration.createProperties
      ? viewService.computeViewProperties(
            configuration.createProperties,
            rowModel.properties,
            {},
            [],
          ).map(decorateTableProperty)
      : properties;
  const configFilter = configuration && configuration.filter
          && Condition(configuration.filter, properties, viewService);
  const headerButtons = configuration
          && configuration.headerButtons
          && viewService.getComponent(configuration.headerButtons);
  const rowButtons = configuration
      && configuration.rowButtons
      && viewService.getComponent(configuration.rowButtons);
  return {
    columns,
    propsByName,
    properties,
    configFilter,
    headerButtons,
    rowButtons,
    createProperties,
    editColumn: configuration && configuration.editColumn || undefined,
  };
};

const createNode = (obj, iRow, { getRowClassName, getRowId, nodesById, columns }) => {
  if (!obj) {
    return null; // Paging
  }
  const id = getRowId(obj, iRow);
  const values = new Array(columns.length);
  const fvalues = new Array(columns.length);
  columns.forEach((col, i) => {
    let value;
    if (col.getValue) {
      value = col.getValue(obj);
    } else if (col.editor && col.editor.getValue) {
      value = col.editor.getValue(obj, col);
    } else {
      value = obj[col.name];
    }
    let na = false;
    if ((value === null) || (value === undefined)) {
      if (col.defaultValue !== undefined) {
        value = col.defaultValue;
      } else {
        na = true;
      }
    }
    values[i] = value;
    let fvalue;
    if (na) {
      fvalue = 'N/A';
    } else if (col.formatValue) {
      fvalue = col.formatValue(value, obj, col.name, col);
    } else if (col.editor) {
      fvalue = col.editor.formatValue(value, obj, col.name, col);
    } else {
      fvalue = value;
    }
    fvalues[i] = fvalue;
  });
  const node = {
    id,
    index: iRow,
    obj,
    values,
    fvalues,
    leaf: true,
    treeDepth: 0,
  };
  if (getRowClassName) {
    node.className = getRowClassName(obj);
  }
  nodesById[id] = node;
  return node;
};

const computeRowsState = (props, state) => {
  const { columns } = state;
  const rowModel = props.models[0];

  const { data, reuseSameObjects, onLoadPage } = rowModel;
  const paging = !!onLoadPage;
  const getRowId = rowModel.getRowId
        || (rowModel.idProperty && (row => row[rowModel.idProperty]))
        || ((row, i) => row.id || row._id || i);

  const nodesById = {};
  const previousNodesById = state.nodesById;
  const createParams = {
    nodesById,
    getRowClassName: props.getRowClassName,
    getRowId,
    columns,
  };
  // let newCount = 0;
  const rowNodes = (data && (paging ? data.data : data) || []).map((obj, iRow) => {
    if (!obj) {
      return null;
    }
    const id = getRowId(obj, iRow);
    const oldNode = reuseSameObjects && previousNodesById && previousNodesById[id];
    if (oldNode && oldNode.obj === obj) {
      nodesById[id] = oldNode;
      oldNode.children = undefined;
      oldNode.leaf = true;
      return oldNode;
    }
    const node = createNode(obj, iRow, createParams);
    node.selected = oldNode ? oldNode.selected : false;
    node.checked = oldNode ? oldNode.checked : false;
    return node;
  });

  // Manage tree table
  let treeTable = false;
  const getParentId = rowModel.parentId ? (
    typeof rowModel.parentId === 'string' && (o => o[rowModel.parentId]) || rowModel.parentId
  ) : undefined;
  if (getParentId) {
    const updateDepth = (dn, depth) => {
      const mdn = dn;
      mdn.treeDepth = depth;
      if (dn.children) {
        dn.children.forEach((cdn) => {
          updateDepth(cdn, depth + 1);
        });
      }
    };
    const { configuration = {} } = props;
    const { collapsedByDefault = false } = configuration;
    rowNodes.forEach((node) => {
      const parentId = node && getParentId(node.obj);
      if (parentId) {
        const n = node;
        const parent = nodesById[parentId];
        n.parent = parent;
        if (parent) {
          treeTable = true;
          const { children } = parent;
          if (children) {
            children.push(node);
          } else {
            parent.leaf = false;
            parent.collapsed = collapsedByDefault;
            parent.children = [node];
          }

          // This approach is optimized for most likely case that tree is of depth 2 max
          // and/or that rows are listed in a way child rows are below parent rows.
          // So the expensive solution below should rarely happen
          if (n.children && n.children.length) {
            updateDepth(n, n.parent.treeDepth + 1);
          } else {
            n.treeDepth = n.parent.treeDepth + 1;
          }
        }
      }
    });
    if (treeTable) {
      const treeColumn = columns.find(col => col.tree) || columns[0];
      treeColumn.tree = true;
      treeColumn.renderProps = ['collapsed'];
      if (treeColumn.origRendererCmp) {
        treeColumn.rendererCmp = withTree(treeColumn.origRendererCmp);
      }
      if (treeColumn.origEditorCmp) {
        treeColumn.editorCmp = withTree(treeColumn.origEditorCmp);
      }
    }
  }
  const { readOnly = false } = rowModel;
  const { canCreate = !readOnly, canDelete = !readOnly, canEdit = !readOnly } = rowModel;
  const { canCheck = canDelete } = rowModel;
  return {
    rowNodes,
    nodesById,
    getRowId,
    readOnly,
    canCreate,
    canDelete,
    canEdit,
    canCheck,
    treeTable,
    loaded: !!data,
    onLoadPage,
    paging,
    pagingRowCount: (paging && data) ? data.filteredCount : 0,
    pagingTotalRowCount: (paging && data) ? data.totalCount : 0,
  };
};

const computeTableFilter = (tableModels, context, columnState) => {
  const processFilter = (filter) => {
    if (filter && (typeof filter === 'string')) {
      const lowFilter = filter.toLowerCase();
      return (s) => {
        if (!s || !s.toLowerCase) {
          return false;
        }
        return s.toLowerCase().indexOf(lowFilter) >= 0;
      };
    }
    return filter;
  };

  const aggregateFilters = (filter1, filter2) => {
    const f1 = processFilter(filter1);
    const f2 = processFilter(filter2);
    if (!f1) return f2 || undefined;
    if (!f2) return f1;
    return value => f1(value) && f2(value);
  };

  const filter = aggregateFilters(
    columnState.configFilter,
    context && context.search,
  );
  if (!filter) {
    return null;
  }
  const rowModel = tableModels[0];
  const { getExtraFilterValues } = rowModel;

  return (node) => {
    const { values, fvalues } = node;
    if (!filter) return true;
    if (getExtraFilterValues
      && getExtraFilterValues(node.obj).find(value => (value !== undefined) && (value !== null) && filter(value))) {
      return true;
    }
    return Boolean(columnState.columns.find(((col, i) => {
      const value = col.editor ? col.editor.getFilterValue(values[i], fvalues[i], col, node.obj) : undefined;
      return (value !== undefined) && (value !== null) && filter(value, i);
    })));
  };
};

const computeFilterState = (props, state) => {
  const filter = computeTableFilter(props.models, props.context, state);
  const { rowNodes, treeTable, paging, pagingRowCount } = state;
  if (!treeTable) {
    const filteredRowNodes = !filter ? rowNodes.slice() : rowNodes.filter(filter);
    return {
      filteredRowNodes,
      pagingRowCount: paging && (pagingRowCount - (rowNodes.length - filteredRowNodes.length)),
    };
  }

  // Tree table
  // filterRowState stores filter state of each node by index.
  // If a state is undefined => node has not been processed yet
  const filterRowState = new Array(rowNodes.length);

  const deepFilterNode = (node) => {
    if (node.children && node.children.length) {
      const n = node;
      n.filteredChildren = node.children.filter(child => deepFilterNode(child));
      if (n.filteredChildren.length) {
        // If one of child node is accepted, parent node is accecpted whatever it would be accepted itself or not
        filterRowState[n.index] = true;
        return true;
      }
      n.filteredChildren = null;
    }
    const accepted = !filter || filter(node);
    filterRowState[node.index] = accepted;
    return accepted;
  };

  return { filteredRowNodes: rowNodes.filter((node, i) => {
    // filteredRowNodes only contain root nodes, accepted children nodes are stored in node.filteredChildren
    return node
        && (filterRowState[i] === undefined ? deepFilterNode(node) : filterRowState[i])
        && !node.parent;
  }) };
};

const sortRows = (rowNodes, sortedColumn, sortOrder, colIndex, treeTable) => {
  if (sortedColumn && (sortedColumn !== NO_SORT_PROPERTY)) {
    const { editor } = sortedColumn;
    // prepare comparison function, for a stable sorting
    const cmp = getSorting(sortOrder, node => node.sortValue);
    const stableCmp = (node1, node2) => {
      const order = cmp(node1, node2);
      if (order !== 0) return order;
      return node1.index - node2.index;
    };

    const sort = (nodes) => {
      // Prepare sorting with storing sortable values into node and fetching root nodes if tree table
      const count = nodes.length;
      for (let i = 0; i < count; i += 1) {
        const n = nodes[i];
        n.sortValue = editor.getSortValue(n.values[colIndex], n.fvalues[colIndex], n.obj);
        if (treeTable) {
          n.sortedChildren = n.filteredChildren && sort(n.filteredChildren);
        }
      }
      const sortedNodes = nodes.slice().sort(stableCmp);
      for (let i = 0; i < count; i += 1) {
        const n = nodes[i];
        n.sortValue = undefined;
      }
      return sortedNodes;
    };
    return sort(rowNodes);
  }
  if (treeTable) {
    const initSortedChildren = (nodes) => {
      nodes.forEach((node) => {
        const n = node;
        n.sortedChildren = n.filteredChildren ? initSortedChildren(n.filteredChildren) : undefined;
      });
      return nodes;
    };
    return initSortedChildren(rowNodes);
  }
  return rowNodes;
};

const computeSortState = (props, state) => {
  const configuration = props.configuration || {};
  const { storageKey } = configuration;
  const { columns, filteredRowNodes, treeTable, paging } = state || {};
  let { sortedColumn, sortOrder } = state;
  let colIndex;
  // Restore/set sorting property
  const sorts = [
    {
      name: sortedColumn && sortedColumn.name,
      order: sortOrder,
    },
    {
      name: storageKey && getStorageItem(props, `${storageKey}-sort`),
      order: storageKey && getStorageItem(props, `${storageKey}-sort-order`),
    },
    {
      name: configuration.sort,
      order: configuration.sortOrder,
    },
  ];
  sortedColumn = null;
  for (let i = 0; !sortedColumn && (i < sorts.length); i += 1) {
    // if (sorts[i].name === '') {
    //   // Empty string marks the user action of removing any sorting on the table
    //   sortedColumn = undefined;
    //   sortOrder = DESC_ORDER;
    //   break;
    // }
    sortedColumn = sorts[i].name && columns.find(c => ((c.name === sorts[i].name) && c.canSort)) || undefined;
    sortOrder = sortedColumn && sorts[i].order || DESC_ORDER;
    colIndex = sortedColumn && columns.indexOf(sortedColumn);
  }
  return {
    sortedColumn,
    sortOrder,
    sortedRowNodes: paging
      ? filteredRowNodes
      : sortRows(filteredRowNodes, sortedColumn, sortOrder, colIndex, treeTable),
  };
};

const computeVisibleNodesFromCollapsedNodes = (props, state, computeHiddenNodes) => {
  const visibleRowNodes = [];
  const hiddenRowNodes = [];
  let tableDepth = 0;
  const addHiddenNodesDeep = (nodes) => {
    nodes.forEach((n) => {
      hiddenRowNodes.push(n);
      if (n.sortedChildren) {
        addHiddenNodesDeep(n.sortedChildren);
      }
    });
  };
  const processChildNodes = (node, depth) => {
    if (depth > tableDepth) {
      tableDepth = depth;
    }
    if (node.sortedChildren) {
      if (!node.collapsed) {
        node.sortedChildren.forEach((child) => {
          if (child) {
            visibleRowNodes.push(child);
            processChildNodes(child, depth + 1);
          }
        });
      } else if (computeHiddenNodes) {
        addHiddenNodesDeep(node.sortedChildren);
      }
    }
  };
  processChildNodes({ sortedChildren: state.sortedRowNodes }, -1);
  return { visibleRowNodes, hiddenRowNodes, tableDepth };
};

const updateRowCount = (props, total, filtered) => {
  const { configuration } = props;
  const fCountProp = configuration.filteredRowCountProperty && props[configuration.filteredRowCountProperty];
  const countProp = configuration.rowCountProperty && props[configuration.rowCountProperty];
  if (fCountProp || countProp) {
    if (countProp === fCountProp) {
      countProp.setRowNumbers(filtered, total);
    } else {
      if (countProp) {
        countProp.setTotalRowCount(total);
      }
      if (fCountProp) {
        fCountProp.setFilteredRowCount(filtered);
      }
    }
  }
};

const computeVisibleNodes = (props, state) => {
  const { sortedRowNodes, nodesById, paging } = state;
  const { configuration = {} } = props;
  if (!state.treeTable) {
    if (paging) {
      updateRowCount(props, state.pagingTotalRowCount, state.pagingRowCount);
    } else {
      updateRowCount(props, state.rowNodes.length, sortedRowNodes.length);
    }
    return { visibleRowNodes: sortedRowNodes };
  }
  // If a tree table, hide descendant of collapsed nodes
  const { storageKey, collapsedByDefault = false } = configuration;
  const collapsedStr = storageKey && getStorageItem(props, `${storageKey}-collapsed-nodes`);
  const collapsedNodes = collapsedStr
      && collapsedStr.split(',').map(id => nodesById[id]).filter(n => n)
      || [];
  if (state.collapsedNodes) {
    state.collapsedNodes.forEach((n) => { const node = n; node.collapsed = collapsedByDefault; });
  }
  collapsedNodes.forEach((n) => { const node = n; node.collapsed = !collapsedByDefault; });
  const { visibleRowNodes, hiddenRowNodes, tableDepth } = computeVisibleNodesFromCollapsedNodes(
    props, state, !state.page && (configuration.filteredRowCountProperty || configuration.rowCountProperty),
  );
  if (paging) {
    updateRowCount(props, state.pagingTotalRowCount,
        state.pagingRowCount - (sortedRowNodes.length - visibleRowNodes.length));
  } else {
    updateRowCount(props, state.rowNodes.length, visibleRowNodes.length + hiddenRowNodes.length);
  }
  return { visibleRowNodes, collapsedNodes, tableDepth };
};

const computeCheckAndSelectedStates = (props, state) => {
  const { getRowId, nodesById, visibleRowNodes } = state;

  const getUpdatedNode = getRowId ? (n) => {
    const newNode = nodesById[n.id];
    return newNode && visibleRowNodes.indexOf(newNode) >= 0 ? newNode : undefined;
  } : (n) => {
    return visibleRowNodes.find(vn => (vn.obj === n.obj));
  };

  const updateNodes = (nodes) => {
    if (!nodes || !nodes.length) {
      return nodes || [];
    }
    const removedNodes = [];
    // Replace selected/checked nodes with nodes with the same ID from new computed rows
    for (let i = 0; i < nodes.length;) {
      const n = nodes[i];
      const newNode = getUpdatedNode(n);
      if (!newNode) {
        removedNodes.push(n);
        nodes.splice(i, 1);
      } else {
        nodes[i] = newNode;
        i += 1;
      }
    }
    return removedNodes;
  };

  //
  // Update selection state
  //
  let newState;
  const unselNodes = updateNodes(state.selected);
  setNodesProperty(unselNodes, 'selected', false);

  // selection specified with props is prioritary
  const propSelectedNodes = getRowId
    ? (props.selected || []).map(o => nodesById[getRowId(o)])
        .filter(n => n && (visibleRowNodes.indexOf(n) >= 0))
    : (props.selected || []).filter(o => visibleRowNodes.find(n => (n.obj === o)));
  // If props selection can be displayed
  if (propSelectedNodes.length) {
    // If previous state selection differs from props selection
    if (unselNodes.length || !sameArrays(propSelectedNodes, state.selected)) {
      setNodesProperty(state.selected, 'selected', false);
      setNodesProperty(propSelectedNodes, 'selected', true);
      newState = { selected: propSelectedNodes };
    } else {
      // else no state change, same selection and nodes in state.selected have been updated
      setNodesProperty(state.selected, 'selected', true);
    }
  } else if (!state.selected || !state.selected.length) {
    const id = state.scroll.defaultSelection;
    let newSel = id && visibleRowNodes.find(n => (n.id === id));
    if (id) {
      state.scroll.defaultSelection = null;
    }
    if (newSel) {
      // Stored selected item is displayed, we select it
      newState = { selected: [newSel] };
      newSel.selected = true;
    } else if (unselNodes.length || props.ensureSelection) {
      // We need a new selection
      newSel = props.ensureSelection && visibleRowNodes.length ? visibleRowNodes.find(n => n) : null; // row nodes can be null
      if (newSel) {
        newState = { selected: [newSel] };
        setNodesProperty(newState.selected, 'selected', true);
      } else {
        newState = { selected: [] };
      }
    } else if (!state.selected) {
      newState = { selected: [] };
    }
     // else there was previously no selection and we don't need to ensure one: no changes
  } else if (unselNodes.length) {
    // Unlikely state whith a multiple selection being filtered to a shorter selection
    newState = { selected: state.selected.slice() };
  }

  if (newState && newState.selected.length && getRowId) {
    // scroll object is pointed to by the state but is not modified with the state.
    // For example, the scrollToSelection flag of this object needs to be updated after
    // rendering and does not belong to an application state.
    state.scroll.scrollToSelection = true;
  }

  //
  // Update checked state
  //
  const uncheckedNodes = updateNodes(state.checked);
  setNodesProperty(uncheckedNodes, 'checked', false);
  if (uncheckedNodes.length) {
    // Some checked row(s) are hidden or not belonging to data anymore
    // We update the checked state
    newState = Object.assign(newState || {}, { checked: state.checked.slice() });
  }

  return newState;
};

const tableWith = (TableComponent, options = {}) => {
  const { autoScrollToSelection = true } = options;
  class TableModel extends React.PureComponent {
    static propTypes = {
      configuration: PropTypes.shape({
        i18n: PropTypes.shape({}),
        rowsPerPage: PropTypes.number,
        cellProperties: PropTypes.arrayOf(PropTypes.string),
        noRowsMessage: PropTypes.string,
      }),
      context: PropTypes.shape({ search: PropTypes.string }),
      models: PropTypes.arrayOf(PropTypes.shape({
        data: PropTypes.oneOfType([
          PropTypes.arrayOf(PropTypes.shape({})),
          PropTypes.shape({}),
        ]),
      })),
      dataService: PropTypes.shape({
        validateValue: PropTypes.func.isRequired,
        getDefaultValue: PropTypes.func.isRequired,
      }).isRequired,
      viewService: PropTypes.shape({
        getIcon: PropTypes.func,
      }).isRequired,
      onSelect: PropTypes.func,
      ensureSelection: PropTypes.bool,
      selected: PropTypes.arrayOf(PropTypes.shape({})),
      getStorageItem: PropTypes.func,
      setStorageItem: PropTypes.func,
      getRowClassName: PropTypes.func,
      MasterComponent: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({}),
      ]),
      DetailsComponent: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({}),
      ]),
      renderDetails: PropTypes.func,
      serverQuery: PropTypes.string,
    }

    static defaultProps = {
      context: undefined,
      configuration: undefined,
      models: [],
      onSelect: undefined,
      ensureSelection: false,
      selected: undefined,
      getStorageItem: undefined,
      setStorageItem: undefined,
      getRowClassName: undefined,
      MasterComponent: undefined,
      DetailsComponent: undefined,
      renderDetails: undefined,
      serverQuery: undefined,
    };

    static getDerivedStateFromProps(props, state = {}) {
      const { configuration } = props;
      let newState;
      let update = false;
      if (!Object.prototype.hasOwnProperty.call(state, 'configuration') || (state.configuration !== configuration)) {
        const rowsPerPage = configuration && configuration.rowsPerPage || DEFAULT_ROWS_PER_PAGE;
        newState = {
          editedRow: undefined,
          editValues: [],
          validationErrors: [],
          page: 0,
          rowsPerPage,
          getScrollElement: (rowsPerPage <= 0) && options.getScrollElement ? options.getScrollElement : () => null,
          confirmDialogOpen: false,
          createDialogOpen: false,
          CreateRowDialog: configuration && ((props || {}).configuration.CreateDialog) || CreateDialog,
          deleteMessage: '',
          deleteTitle: 'Confirm Delete',
          checked: [],
          configuration,
          noRowsMessage: configuration.noRowsMessage,
          ...computeColumnsState(props, options),
        };
        update = true;
      }
      const rows = props.models[0].data;
      if (update
            || (state.rowModel !== props.models[0])
            || (state.rows !== props.models[0].data)) {
        newState = Object.assign(newState || {}, {
          rowModel: props.models[0],
          rows,
          ...computeRowsState(props, Object.assign({}, state, newState)),
        });
        update = true;
      }
      const contextFilter = props.context ? props.context.search : undefined;
      if (update || (state.contextFilter !== contextFilter)) {
        newState = Object.assign(newState || {}, {
          contextFilter,
          ...computeFilterState(props, Object.assign({}, state, newState)),
        });
        update = true;
      }

      if (configuration && configuration.cellProperties) {
        const newCellProps = toObject(configuration.cellProperties, props, state.cellProps);
        if (update || (newCellProps !== state.cellProps)) {
          newState = Object.assign(newState || {}, { cellProps: newCellProps });
          update = true;
        }
      }

      if (update) {
        newState = Object.assign(newState || {}, computeSortState(props, Object.assign({}, state, newState)));
        newState = Object.assign(newState, { ...computeVisibleNodes(props, Object.assign({}, state, newState)) });
      }
      const selState = computeCheckAndSelectedStates(props, Object.assign({}, state, newState), options);
      if (selState) {
        newState = newState && Object.assign(newState, selState) || selState;
        if (selState.selected && props.onSelect
                && !sameArrays(props.selected, selState.selected.map(n => n.obj))) {
          setTimeout(() => {
            props.onSelect(selState.selected.map(n => n.obj));
          });
        }
      }
      return newState || null;
    }

    constructor(props) {
      super(props);
      const { storageKey, saveSelection = true } = props.configuration || {};
      this.scroll = {
        defaultSelection: storageKey && saveSelection && getStorageItem(props, `${storageKey}-selection`),
        scrollToSelection: false,
      };
      this.state = {
        scroll: this.scroll, // static object referred to from getDerivedStateFromProps
      };

      this.handleCreate = this.handleCreate.bind(this);
      this.handleDelete = this.handleDelete.bind(this);
      this.handleConfirmDelete = this.handleConfirmDelete.bind(this);
      this.handleConfirmAdd = this.handleConfirmAdd.bind(this);
      this.handleSelectClick = this.handleSelectClick.bind(this);
      this.handleCheckClick = this.handleCheckClick.bind(this);
      this.handleCheckAllClick = this.handleCheckAllClick.bind(this);
      this.handleChangePage = this.handleChangePage.bind(this);
      this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
      this.getString = this.getString.bind(this);
      this.getIcon = this.getIcon.bind(this);
      this.getDefaultRowValues = this.getDefaultRowValues.bind(this);
      this.startEditing = this.startEditing.bind(this);
      this.cancelEditing = this.cancelEditing.bind(this);
      this.stopEditing = this.stopEditing.bind(this);
      this.handleCellEditing = this.handleCellEditing.bind(this);
      this.handleUpdateRow = this.handleUpdateRow.bind(this);
      this.handleSort = this.handleSort.bind(this);
      this.handleToggleCollapse = this.handleToggleCollapse.bind(this);
      this.validateValue = this.validateValue.bind(this);

      const { configuration = {} } = props;
      this.tableHandler = {
        onSelectClick: this.handleSelectClick,
        onCheckClick: this.handleCheckClick,
        onCheckAllClick: this.handleCheckAllClick,
        onChangePage: this.handleChangePage,
        onChangeRowsPerPage: this.handleChangeRowsPerPage,
        getString: this.getString,
        getIcon: this.getIcon,
        getDefaultValues: this.getDefaultValues,
        validateValue: this.validateValue,
        onStartEditing: this.startEditing,
        onCancelEditing: this.cancelEditing,
        onStopEditing: this.stopEditing,
        onUpdateRow: this.handleUpdateRow,
        onEditCell: this.handleCellEditing,
        onCreate: this.handleCreate,
        onDelete: this.handleDelete,
        onSort: this.handleSort,
        onToggleCollapse: this.handleToggleCollapse,
        getRows: () => this.state.rows,
        getTableRowId: configuration.getTableRowId,
        isLoaded: () => this.state.loaded,
        isRowLoaded: ({ index }) => {
          return !!this.state.visibleRowNodes[index];
        },
        onLoadPage: ({ startIndex, stopIndex }) => {
          // Invoked when scrolling the table
          // this.state provides columns and sorting
          this.state.onLoadPage({
              ...this.state,
              startIndex,
              stopIndex,
              serverQuery: this.props.serverQuery,
            }).then(() => {
            const { rowNodes, ...createProps } = this.state;
            createProps.getRowClassName = this.props.getRowClassName;
            const { data } = this.props.models[0];
            for (let i = startIndex; i <= stopIndex; i += 1) {
              rowNodes[i] = createNode(data.data[i], i, createProps);
            }
            this.setState({
              visibleRowNodes: rowNodes.slice(),
            });
          });
        },
        isPaging: () => this.state.paging,
        getPagingRowCount: () => this.state.pagingRowCount,
      };
      this.tableRef = React.createRef();
    }

    getSnapshotBeforeUpdate() {
      const { getScrollElement } = this.state;
      const scrollElt = getScrollElement && this.tableRef.current && getScrollElement(this.tableRef.current);
      return scrollElt ? { scrollTop: scrollElt.scrollTop, scrollHeight: scrollElt.scrollHeight } : null;
    }

    componentDidMount() {
      if (!this.state.loaded) {
        this.load();
      } else {
        // If we have a details component at the bottom, it is not updated yet at this moment
        // => the table height will change, impacting the scroll position to the selection
        this.updateScroll({
          scrollTop: 0,
          delay: !!this.props.DetailsComponent || !!this.props.renderDetails,
        });
      }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (this.state.paging
          && (((!this.props.models[0].data && prevProps.models[0].data)) // Data can be cleaned after a sort operation, reload
                || (this.props.serverQuery !== prevProps.serverQuery))) { // If filtering has changed, reload
        this.load();
      } else {
        this.updateScroll(snapshot);
      }
    }

    componentWillUnmount() {
      const { storageKey, saveSelection = true } = this.props.configuration || {};
      const { getRowId, selected } = this.state;
      if (storageKey && saveSelection && getRowId && selected && selected.length) {
        setStorageItem(this.props, `${storageKey}-selection`, selected[0].id);
      }
    }

    // shouldComponentUpdate(nextProps, nextState) {
    //   console.log('tableWith: shouldComponentUpdate');
    //   let update = false;
    //   Object.keys(this.props).forEach((key) => {
    //     if (this.props[key] !== nextProps[key]) {
    //       update = true;
    //       console.log(`          ${key}: ${this.props[key]} => ${nextProps[key]}`);
    //     }
    //   });
    //   return update;
    // }

    getNode(row) {
      const { nodesById, getRowId } = this.state;
      const id = getRowId ? getRowId(row) : undefined;
      return (id === undefined) || (id === null) ? undefined : nodesById[id];
    }

    // Paging
    load() {
      // Invoked when the component is mounted and no data is provided
      // Or when the user changes the sorting
      const { onLoadPage, sortedColumn, sortOrder, columns } = this.state;
      this.setState({ loaded: false }, () => {
        // A clear parameter will trigger a re-render of the table with new data and
        // thus update the loaded state value.
        onLoadPage({
          clear: true,
          sortedColumn,
          sortOrder,
          columns,
          serverQuery: this.props.serverQuery,
        });
      });
    }

    //
    // Adding, removing, modifying objects
    //

    startEditing(row) {
      const { columns, propsByName } = this.state;
      const editValues = new Array(columns.length);
      const validationErrors = new Array(columns.length);
      const node = this.getNode(row);
      if (node) {
        const { values } = node;
        columns.forEach((col, iCol) => {
          const value = values[iCol];
          editValues[iCol] = value;
          validationErrors[iCol] = this.validateValue(value, propsByName[col.name], row);
        });
        this.setState({
          editValues,
          validationErrors,
          editedRow: node,
        });
      }
    }

    hasEditErrors() {
      return !!this.state.validationErrors.find(e => e);
    }

    stopEditing() {
      if (this.hasEditErrors()) {
        this.cancelEditing();
        return;
      }
      const values = this.state.editValues;
      const rowNode = this.state.editedRow;
      const { columns, rowModel } = this.state;
      const { onUpdateObject } = rowModel;
      this.setState({ editValues: [], validationErrors: [], editedRow: undefined }, () => {
        if (onUpdateObject) {
          const newRow = Object.assign({}, rowNode.obj);
          columns.forEach((col, iCol) => {
            if (col.setValue) {
              col.setValue(newRow, values[iCol]);
            } else {
              newRow[col.name] = values[iCol];
            }
          });
          onUpdateObject(rowNode.obj, rowNode.index, newRow);
        }
      });
    }

    cancelEditing() {
      this.setState({ editValues: [], validationErrors: [], editedRow: undefined });
    }

    validateValue(value, property, row) {
      const model = this.state.rowModel;
      const validationCtx = {
        ...this.props,
        object: row,
      };
      const { dataService } = this.props;
      return dataService.validateValue(value, property, model.data, validationCtx);
    }

    handleCellEditing = (value, col, iCol) => {
      const { editValues, validationErrors, editedRow, propsByName } = this.state;
      const eventToValue = (e) => {
        return e && e.target ? e.target.value : e;
      };
      const v = eventToValue(value);
      const newEditValues = editValues.slice();
      newEditValues[iCol] = v;
      const newErrors = validationErrors.slice(0);
      newErrors[iCol] = this.validateValue(v, propsByName[col.name], editedRow.obj);
      this.setState({
        editValues: newEditValues,
        validationErrors: newErrors,
      });
    }

    handleUpdateRow(row, newProps) {
      const rowNode = this.getNode(row);
      const { rowModel } = this.state;
      const { onUpdateObject } = rowModel;
      this.setState({ editValues: [], validationErrors: [], editedRow: undefined }, () => {
        if (onUpdateObject) {
          const newRow = Object.assign({}, row, newProps);
          onUpdateObject(row, rowNode.index, newRow);
        }
      });
    }

    handleDelete = () => {
      const { checked, rowModel } = this.state;
      const { onDeleteObject, validateDelete } = rowModel;
      if (onDeleteObject && checked.length) {
        const messages = [];
        let toDelete = checked.map(node => node.obj);
        if (validateDelete) {
          toDelete = toDelete.filter((obj) => {
            const msg = validateDelete(obj);
            if (msg) {
              messages.push(msg);
            }
            return !msg;
          });
        }
        let deleteMessage;
        let deleteTitle = this.getString('confirmDeleteTitle', 'Confirm delete');
        let deleteButtons = [{ title: 'Cancel', value: false }, { title: this.getString('confirmDeleteButton', `Delete ${this.getString('rowOrRows', 'row(s)')}`), value: toDelete }];
        if (messages.length === checked.length) {
          deleteTitle = this.getString('deleteWarning', 'Delete warning');
          deleteMessage = this.getString('invalidRowsToDelete', messages[0]);
          deleteButtons = [{ title: 'Close', value: false }];
        } else if (messages.length) {
          deleteMessage = this.getString('confirmPartialDelete', `Some ${this.getString('rowOrRows', 'row(s)')} cannot be deleted, do you want to continue?`);
        } else {
          deleteMessage = this.getString('confirmDelete', `Are you sure you want to delete the ${checked.length} checked ${this.getString('rowOrRows', 'row(s)')}?`);
        }
        this.setState({
          confirmDialogOpen: true,
          deleteTitle,
          deleteMessage,
          deleteButtons,
        });
      }
    }

    handleConfirmDelete = (toDelete) => {
      const { rowModel } = this.state;
      if (toDelete) {
        const { onDeleteObject } = rowModel;
        onDeleteObject(toDelete);
        this.setState({ checked: [], selected: [], confirmDialogOpen: false });
      } else {
        this.setState({ confirmDialogOpen: false });
      }
    }

    handleCreate = () => {
      const { configuration = {} } = this.props;
      const { onCreate } = configuration;
      if (onCreate) {
        onCreate(this.props);
      } else {
        this.setState({
          createDialogOpen: true,
        });
      }
    }

    handleConfirmAdd = (newObject) => {
      if (newObject) {
        const { onAddObject } = this.state.rowModel;
        onAddObject(newObject);
      }
      this.setState({ createDialogOpen: false });
    }

    //
    // Selection
    //

    setSelected(selected, userSelect) {
      const { onSelect } = this.props;
      if (onSelect) {
        onSelect(selected.map(n => n.obj), userSelect);
      }
      if (!this.isControlledSelection()) {
        setNodesProperty(this.state.selected, 'selected', false);
        setNodesProperty(selected, 'selected', true);
        if (selected && selected.length) {
          this.scroll.scrollToSelection = true;
        }
        this.setState({ selected });
      }
    }

    isControlledSelection() {
      return !!this.props.selected;
    }

    handleSelectClick = (event, row) => {
      const rowNode = this.getNode(row);
      if (!this.state.editedRow && rowNode) {
        const { selected } = this.state;
        const selectedIndex = selected.indexOf(rowNode);
        let newSelected = [];
        const multiSelect = event.metaKey || event.ctrlKey;

        if (multiSelect) {
          if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, rowNode);
          } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
          } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
          } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
              selected.slice(0, selectedIndex),
              selected.slice(selectedIndex + 1),
            );
          }
        } else {
          newSelected = [rowNode];
        }

        this.setSelected(newSelected, true);
      }
    };

    //
    // Check
    //

    setChecked(checked) {
      this.setState((prevState) => {
        const { rowModel } = prevState;
        const prevChecked = prevState.checked;
        if (prevChecked) {
          for (let i = 0; i < prevChecked.length; i += 1) {
            prevChecked[i].checked = false;
          }
        }
        const validatedChecks = rowModel.validateChecks
            ? rowModel.validateChecks(checked) : checked;
        for (let i = 0; i < validatedChecks.length; i += 1) {
          const chk = validatedChecks[i];
          chk.checked = true;
        }
        return { checked: validatedChecks };
      });
    }

    handleCheckAllClick = (event) => {
      if (event.target.checked) {
        const { visibleRowNodes } = this.state;
        this.setChecked(visibleRowNodes);
      } else {
        this.setChecked([]);
      }
    };

    handleCheckClick = (event, row) => {
      const rowNode = this.getNode(row);
      if (!this.state.editedRow && rowNode) {
        const { checked } = this.state;
        const checkIndex = checked.indexOf(rowNode);
        let newChecked = [];

        if (checkIndex === -1) {
          newChecked = checked.concat(rowNode);
        } else if (checkIndex === 0) {
          newChecked = newChecked.concat(checked.slice(1));
        } else if (checkIndex === checked.length - 1) {
          newChecked = newChecked.concat(checked.slice(0, -1));
        } else if (checkIndex > 0) {
          newChecked = newChecked.concat(
            checked.slice(0, checkIndex),
            checked.slice(checkIndex + 1),
          );
        }

        this.setChecked(newChecked);
      }
    };

    //
    // Sorting
    //
    handleSort = (column) => {
      let sortOrder = DESC_ORDER;
      let sortedColumnName = column.name;
      const prevSortedColumnName = this.state.sortedColumn && this.state.sortedColumn.name;
      const { filteredRowNodes, treeTable } = this.state;

      if (prevSortedColumnName === sortedColumnName) {
        if (this.state.sortOrder === DESC_ORDER) {
          sortOrder = ASC_ORDER;
        } else {
          const { threeStatesSorting = true } = this.props.configuration || {};
          if (threeStatesSorting) {
            sortedColumnName = undefined;
          }
        }
      }
      const { storageKey } = this.props.configuration || {};
      if (storageKey) {
        setStorageItem(this.props, `${storageKey}-sort`, sortedColumnName || '');
        setStorageItem(this.props, `${storageKey}-sort-order`, sortedColumnName && sortOrder || DESC_ORDER);
      }
      const sortedColumn = sortedColumnName ? column : NO_SORT_PROPERTY;
      const { paging, columns, selected } = this.state;
      const colIndex = sortedColumnName ? columns.indexOf(column) : -1;
      // Clear selection state before sorting
      if (selected && selected.length) {
        selected.forEach((s) => {
          s.selected = false;
        });
      }

      if (paging) {
        this.setState({ sortOrder, sortedColumn, selected: undefined, loaded: false }, () => {
          this.load();
        });
      } else {
        const state = {
          sortOrder,
          sortedColumn,
          sortedRowNodes: sortRows(filteredRowNodes, sortedColumn, sortOrder, colIndex, treeTable),
          selected: undefined,
        };
        if (!this.state.treeTable) {
          state.visibleRowNodes = state.sortedRowNodes;
        } else {
          Object.assign(
            state,
            computeVisibleNodesFromCollapsedNodes(
              this.props, Object.assign({}, this.state, state), this.state.collapsedNodes,
            ),
          );
        }
        this.scroll.scrollToSelection = false;
        this.setState(state);
      }
    }

    //
    // Tree table
    //
    handleToggleCollapse(object) {
      const { storageKey, collapsedByDefault = false } = this.props.configuration || {};
      const { collapsedNodes } = this.state;
      const rowNode = this.getNode(object);
      const index = collapsedNodes.indexOf(rowNode);
      if (index >= 0) {
        rowNode.collapsed = collapsedByDefault;
        collapsedNodes.splice(index, 1);
      } else {
        rowNode.collapsed = !collapsedByDefault;
        collapsedNodes.push(rowNode);
      }
      if (storageKey) {
        setStorageItem(this.props,
          `${storageKey}-collapsed-nodes`, collapsedNodes.map(n => n.id).join(','));
      }
      const newState = computeVisibleNodesFromCollapsedNodes(this.props, this.state, collapsedNodes);
      this.setState({ ...newState });
    }

    //
    // Paging
    //
    handleChangePage = (event, page) => {
      this.setState({ page });
    };

    handleChangeRowsPerPage = (event) => {
      this.setState({ rowsPerPage: event.target.value });
    };

    // Get localized strings
    getString(key, defaultValue) {
      const { configuration } = this.props;
      const localized = configuration && configuration.i18n && configuration.i18n[key];
      if (!localized) return defaultValue;
      if (typeof localized === 'string') {
        return localized;
      }
      const { checked } = this.state;
      return localized({ ...this.props, checked: checked.map(n => n.obj) });
    }

    getIcon(key) {
      return typeof key === 'string' ? this.props.viewService.getIcon(key) : key;
    }

    // Get default values for creating new row object
    getDefaultRowValues() {
      const { dataService } = this.props;
      const { createProperties, rowModel } = this.state;
      const defaultValues = {};
      createProperties.forEach((property) => {
        defaultValues[property.name] = dataService.getDefaultValue(property, rowModel.data, rowModel);
      });
      return defaultValues;
    }

    // Scrolling
    scrollToElement(elt, { scrollTop, scrollElt }) {
      const rect = elt.getBoundingClientRect();
      const scrollRect = scrollElt.getBoundingClientRect();
      const parentElt = elt.parentElement;
      const parentRect = parentElt.getBoundingClientRect();
      // Minimum scrollTop is when bottom of selection is at the bottom of the viewport
      const minScrollTop = Math.max(0, scrollTop + rect.bottom - scrollRect.bottom);
      // Maximum scrollTop is when top of selection is at the top of the viewport
      const maxScrollTop = rect.top - parentRect.top;
      // If possible to not change the scroll, don't change it
      if (minScrollTop > scrollTop) {
        scrollElt.scrollTop = minScrollTop;
      } else if (maxScrollTop < scrollTop) {
        scrollElt.scrollTop = maxScrollTop;
      }
    }

    updateScroll(snapshot) {
      const { configuration = {} } = this.props;
      const { getScrollElement } = this.state;
      const scrollElt = snapshot && getScrollElement && this.tableRef.current && getScrollElement(this.tableRef.current);
      if (scrollElt && this.scroll.scrollToSelection) {
        this.scroll.scrollToSelection = false;
        if (autoScrollToSelection) {
          // Otherwise done with table implementation using getTableImplProps
          if (this.state.selected && this.state.selected.length) {
            const rowId = configuration.getTableRowId && configuration.getTableRowId(this.state.selected[0]);
            const elt = rowId && document.getElementById(rowId);
            if (elt) {
              if (snapshot.delay) {
                setTimeout(() => {
                  this.scrollToElement(elt, { scrollTop: snapshot.scrollTop, scrollElt });
                });
              } else {
                this.scrollToElement(elt, { scrollTop: snapshot.scrollTop, scrollElt });
              }
            }
          }
        }
      }
    }

    // Render
    render() {
      const tableImplProps = options.getTableImplProps && options.getTableImplProps(this) || {};
      const {
        context,
        models,
        renderDetails,
        DetailsComponent,
        MasterComponent,
        ...props
      } = this.props;

      const {
        visibleRowNodes,
        loaded,
        createProperties,
        confirmDialogOpen,
        canCreate,
        createDialogOpen,
        CreateRowDialog,
        columns,
        editColumn,
        headerButtons,
        rowButtons,
        page,
        rowsPerPage,
        selected,
        checked,
        sortedColumn,
        sortOrder,
        editedRow,
        editValues,
        validationErrors,
        canDelete,
        canEdit,
        canCheck,
        treeTable,
        tableDepth,
        readOnly,
        noRowsMessage,
      } = this.state;

      if (!visibleRowNodes.length && noRowsMessage && loaded) {
        return (
          <div className="grb-no-rows-message">
            <Typography variant="h4">{noRowsMessage}</Typography>
          </div>
        );
      }

      if (!loaded) {
        return (
          <div className="grb-no-rows-message">
            <Typography variant="h4">Loading...</Typography>
          </div>
        );
      }

      const hasSelection = selected ? selected.length === 1 : false;
      let Table = (
        <TableComponent
          {...props}
          ref={this.tableRef}
          cellProperties={this.state.cellProps}
          tableHandler={this.tableHandler}
          columns={columns}
          editColumn={editColumn}
          rowNodes={visibleRowNodes}
          headerButtons={headerButtons}
          rowButtons={rowButtons}
          page={page}
          rowsPerPage={rowsPerPage}
          selected={selected}
          checked={checked}
          sortedColumn={sortedColumn}
          sortOrder={sortOrder}
          editedRow={editedRow}
          editValues={editValues}
          validationErrors={validationErrors}
          canCreate={canCreate}
          canDelete={canDelete}
          canEdit={canEdit}
          canCheck={canCheck}
          treeTable={treeTable}
          tableDepth={tableDepth}
          configuration={this.props.configuration || undefined}
          readOnly={readOnly}
          {...tableImplProps}
        />
      );
      if (MasterComponent) {
        Table = (
          <MasterComponent {...props}>
            {Table}
          </MasterComponent>
        );
      }

      return (
        <React.Fragment>
          {(!!renderDetails || !!DetailsComponent) ? (
            <div className="grb-tabledetails-ctnr">
              {Table}
              {hasSelection && (
                <React.Fragment>
                  <div className="grb-tabledetails-divider-hr" />
                  <div className="grb-details-ctnr">
                    {renderDetails ? (
                      renderDetails({
                        ...props,
                        data: selected[0].obj,
                        onSelect: undefined,
                      })
                    ) : (
                      <DetailsComponent
                        {...props}
                        data={selected[0].obj}
                        onSelect={undefined}
                      />
                    )}
                  </div>
                </React.Fragment>
              )}
            </div>
          ) : Table}
          {confirmDialogOpen && (
            <ConfirmDialog
              title={this.state.deleteTitle}
              message={this.state.deleteMessage}
              onClose={this.handleConfirmDelete}
              buttons={this.state.deleteButtons}
              open={confirmDialogOpen}
            />
          )}
          {canCreate && (
            <CreateRowDialog
              {...this.props}
              onClose={this.handleConfirmAdd}
              open={createDialogOpen}
              properties={createProperties}
              validateValue={this.validateValue}
              getDefaultValues={this.getDefaultRowValues}
              configuration={this.props.configuration || undefined}
            />
          )}
        </React.Fragment>
      );
    }
  }
  return TableModel;
};

export {
  tableWith as default,
  NO_SORT_PROPERTY,
  computeColumnsState,
  computeRowsState,
  computeTableFilter,
  sameArrays,
};
